import { useCallback, useEffect, useState } from 'react';
import { Paperclip, Plus, ShieldCheck, Undo2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useAuthStore } from '../stores/authStore';
import { Role } from '../auth/roles';
import { formatEUR, formatDate } from '../lib/format';

export interface CreditRelease {
  id: string;
  amount: number;
  reason: string;
  status: 'ACTIVE' | 'REVERSED' | 'EXPIRED';
  expiresAt: string;
  createdByName: string | null;
  createdAt: string;
  reversedAt: string | null;
  reversalReason: string | null;
  effective: boolean;
  // Snapshots taken at grant time — displayed as recorded, never recomputed from live balances.
  creditLimitAtGrant: number | null;
  computedExposure: number | null;
  exposureBefore: number | null;
  exposureAfter: number | null;
  document: {
    path: string;
    hash: string;
    filename: string;
    mime: string | null;
    size: number | null;
  } | null;
}

export interface CreditPicture {
  /** null = explicitly unlimited (migration 079); 0 = no credit. */
  creditLimit: number | null;
  unlimited: boolean;
  computed: { currentBalance: number; reservedCredit: number; total: number };
  manualRelease: { total: number; entries: CreditRelease[] };
  available: number;
}

/**
 * Manual credit release panel.
 *
 * Renders the three quantities as three separate lines — limit, COMPUTED exposure, manual
 * release — because they are three different things with three different owners. The computed
 * exposure belongs to the money flow (orders open, BLs not invoiced, invoices unpaid); the
 * release is a human override sitting on top of it, with an author, a reason and an expiry.
 * Collapsing them into one "balance" would make the override untraceable and unreversible.
 */
export default function CustomerCreditReleases({
  customerId,
  onChanged,
}: {
  customerId: string;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const role = useAuthStore((s) => s.user?.role);
  const canRelease = role === Role.ACCOUNTING || role === Role.ADMIN || role === Role.OWNER;

  const [picture, setPicture] = useState<CreditPicture | null>(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(() => {
    apiClient
      .get<CreditPicture>(`/customers/${customerId}/credit`)
      .then((r) => setPicture(r.data))
      .catch(showApiError);
  }, [customerId, showApiError]);

  useEffect(load, [load]);

  const grant = async () => {
    setBusy(true);
    try {
      // The server computes the SHA-256 itself from the bytes it stores — the client never sends
      // a digest, so a wrong or forged one cannot be recorded.
      const proofPayload = proof
        ? {
            proof: {
              filename: proof.name,
              contentBase64: await fileToBase64(proof),
              mime: proof.type || undefined,
            },
          }
        : {};
      await apiClient.post(
        `/customers/${customerId}/credit-releases`,
        {
          amount: Number(amount),
          reason: reason.trim(),
          // Sent as an instant so the server applies its own 90-day ceiling in one place.
          ...(expiresAt ? { expiresAt: new Date(`${expiresAt}T12:00:00Z`).toISOString() } : {}),
          ...proofPayload,
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('relations.creditReleases.toastGranted') });
      setOpen(false);
      setAmount('');
      setReason('');
      setExpiresAt('');
      setProof(null);
      load();
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Re-hash the stored document and compare with the digest recorded at grant time. */
  const verify = async (r: CreditRelease) => {
    setVerifying(r.id);
    try {
      const { data } = await apiClient.get<{
        status: 'MATCH' | 'MISMATCH' | 'NO_DOCUMENT' | 'UNAVAILABLE';
        expectedHash: string | null;
        actualHash: string | null;
      }>(`/customers/${customerId}/credit-releases/${r.id}/verify`);
      const tone =
        data.status === 'MATCH' ? 'success' : data.status === 'MISMATCH' ? 'error' : 'info';
      const message =
        data.status === 'MATCH'
          ? t('relations.creditReleases.verifyMatch')
          : data.status === 'MISMATCH'
            ? t('relations.creditReleases.verifyMismatch', {
                expected: data.expectedHash?.slice(0, 16),
                actual: data.actualHash?.slice(0, 16),
              })
            : data.status === 'NO_DOCUMENT'
              ? t('relations.creditReleases.verifyNoDocument')
              : t('relations.creditReleases.verifyUnavailable');
      push({ tone, message });
    } catch (err) {
      showApiError(err);
    } finally {
      setVerifying(null);
    }
  };

  const reverse = async (r: CreditRelease) => {
    const ok = await confirm({
      title: t('relations.creditReleases.reverseTitle', { amount: formatEUR(r.amount) }),
      description: t('relations.creditReleases.reverseDescription'),
      confirmLabel: t('relations.creditReleases.reverse'),
      tone: 'danger',
    });
    if (!ok) return;
    const why = window.prompt(t('relations.creditReleases.reversePrompt'))?.trim() ?? '';
    if (why.length < 10) {
      push({ tone: 'error', message: t('relations.creditReleases.reverseReasonRequired') });
      return;
    }
    try {
      await apiClient.post(
        `/customers/${customerId}/credit-releases/${r.id}/reverse`,
        { reason: why },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('relations.creditReleases.toastReversed') });
      load();
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  if (!picture) return null;
  const { computed, manualRelease, creditLimit, unlimited, available } = picture;
  const entries = manualRelease.entries;

  return (
    <section className="border rounded p-4 bg-card">
      {confirmDialog}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> {t('relations.creditReleases.title')}
        </h2>
        {canRelease && !open && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> {t('relations.creditReleases.releaseCredit')}
          </Button>
        )}
      </div>

      {/* The three quantities, kept visibly apart. */}
      <dl className="text-sm space-y-1">
        <Row
          label={t('relations.creditReleases.creditLimit')}
          value={unlimited ? t('relations.creditReleases.noCeiling') : formatEUR(creditLimit)}
        />
        <Row
          label={t('relations.creditReleases.computedExposure')}
          value={formatEUR(computed.total)}
          hint={t('relations.creditReleases.computedHint', {
            reserved: formatEUR(computed.reservedCredit),
            outstanding: formatEUR(computed.currentBalance),
          })}
        />
        <Row
          label={t('relations.creditReleases.manualRelease')}
          value={manualRelease.total > 0 ? `+ ${formatEUR(manualRelease.total)}` : '—'}
          hint={manualRelease.total > 0 ? t('relations.creditReleases.manualHint') : undefined}
        />
        <Row
          label={t('relations.creditReleases.available')}
          value={unlimited ? '—' : formatEUR(available)}
          strong
        />
      </dl>

      {open && (
        <div className="mt-4 grid gap-3 sm:grid-cols-3 border-t pt-3">
          <div className="space-y-1">
            <Label>{t('relations.creditReleases.amount')}</Label>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          </div>
          <div className="space-y-1">
            <Label>{t('relations.creditReleases.expires')}</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">
              {t('relations.creditReleases.expiresHint')}
            </p>
          </div>
          <div className="space-y-1 sm:col-span-3">
            <Label>{t('relations.creditReleases.reason')}</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('relations.creditReleases.reasonPlaceholder')}
            />
          </div>
          <div className="space-y-1 sm:col-span-3">
            <Label>{t('relations.creditReleases.proof')}</Label>
            <Input
              type="file"
              className="h-auto py-1.5 file:mr-3 file:cursor-pointer file:rounded file:bg-secondary file:px-2 file:py-1 file:text-secondary-foreground"
              onChange={(e) => setProof(e.target.files?.[0] ?? null)}
            />
            <p className="text-[11px] text-muted-foreground">
              {t('relations.creditReleases.proofHint')}
            </p>
          </div>
          <div className="sm:col-span-3 flex gap-2">
            <Button
              size="sm"
              disabled={busy || !(Number(amount) > 0) || reason.trim().length < 10}
              onClick={() => void grant()}
            >
              <ShieldCheck className="h-4 w-4" />
              {busy ? t('relations.creditReleases.granting') : t('relations.creditReleases.grant')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
              {t('relations.creditReleases.cancel')}
            </Button>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="text-left">
                <th className="py-1 font-medium">{t('relations.creditReleases.colAmount')}</th>
                <th className="py-1 font-medium">{t('relations.creditReleases.colReason')}</th>
                <th className="py-1 font-medium">{t('relations.creditReleases.colExposure')}</th>
                <th className="py-1 font-medium">{t('relations.creditReleases.colBy')}</th>
                <th className="py-1 font-medium">{t('relations.creditReleases.colGranted')}</th>
                <th className="py-1 font-medium">{t('relations.creditReleases.colExpires')}</th>
                <th className="py-1 font-medium">{t('relations.creditReleases.colStatus')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="py-1.5 font-medium">{formatEUR(r.amount)}</td>
                  <td className="py-1.5 max-w-[24rem]">
                    {r.reason}
                    {r.document ? (
                      <span className="mt-0.5 block text-muted-foreground">
                        <Paperclip className="mr-1 inline h-3 w-3" />
                        {r.document.filename}
                        {/* The hash, not just the name: this is what proves the stored file is
                            the one attached at the time. */}
                        <code className="ml-1 break-all text-[10px]">
                          sha256:{r.document.hash.slice(0, 16)}…
                        </code>
                      </span>
                    ) : (
                      <span className="mt-0.5 block text-[11px] text-muted-foreground">
                        {t('relations.creditReleases.noDocument')}
                      </span>
                    )}
                    {r.reversalReason && (
                      <span className="block text-muted-foreground">
                        {t('relations.creditReleases.reversed', { reason: r.reversalReason })}
                      </span>
                    )}
                  </td>
                  {/* Read as recorded — these are snapshots, never recomputed from live balances. */}
                  <td className="py-1.5 whitespace-nowrap">
                    {r.exposureBefore != null && r.exposureAfter != null ? (
                      <>
                        {formatEUR(r.exposureBefore)} → {formatEUR(r.exposureAfter)}
                        {r.creditLimitAtGrant != null && (
                          <span className="block text-[10px] text-muted-foreground">
                            {t('relations.creditReleases.limitAt', {
                              amount: formatEUR(r.creditLimitAtGrant),
                            })}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-1.5">{r.createdByName ?? '—'}</td>
                  <td className="py-1.5">{formatDate(r.createdAt)}</td>
                  <td className="py-1.5">{formatDate(r.expiresAt)}</td>
                  <td className="py-1.5">
                    <span
                      className={
                        r.effective
                          ? 'rounded bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-800'
                          : 'rounded bg-muted px-1.5 py-0.5 text-muted-foreground'
                      }
                    >
                      {r.effective
                        ? t('relations.creditReleases.inForce')
                        : t(`relations.creditReleases.status.${r.status}`, {
                            defaultValue: r.status,
                          })}
                    </span>
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {canRelease && r.document && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={verifying === r.id}
                        onClick={() => void verify(r)}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        {verifying === r.id
                          ? t('relations.creditReleases.checking')
                          : t('relations.creditReleases.verify')}
                      </Button>
                    )}
                    {canRelease && r.effective && (
                      <Button size="sm" variant="outline" onClick={() => void reverse(r)}>
                        <Undo2 className="h-3.5 w-3.5" />{' '}
                        {t('relations.creditReleases.reverseShort')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Read a File as bare base64 (FileReader yields a `data:…;base64,` URL). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error(i18n.t('relations.creditReleases.readFileError')));
    reader.readAsDataURL(file);
  });
}

function Row({
  label,
  value,
  hint,
  strong,
}: {
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">
        {label}
        {hint && <span className="block text-[11px]">{hint}</span>}
      </dt>
      <dd className={strong ? 'font-semibold' : ''}>{value}</dd>
    </div>
  );
}
